import * as service from "../service";
import { store } from "../../store";
import type { ServiceResponse } from "../types";

export type RedeemedSponsorTask = {
  /** `sponsor_tasks.id` */
  id: string;
  title?: string | null;
  type?: string | null;
  completed?: boolean | null;
  /** `sponsor_task_completions.id` — the id the proof endpoints expect */
  sponsor_task_completion_id?: string | null;
  proof?: unknown;
  queue_job_status?: string | null;
  queue_job_id?: string | null;
  review_count?: number | null;
  utime?: string | null;
};

export type RedeemedSponsorActivity = {
  id: string;
  sponsor_id?: string | null;
  student_id?: string | null;
  activity_name?: string | null;
  activity_date?: string | null;
  signed_agreement?: string | null;
  type?: string | null;
  official_ig_account?: string | null;
  committee_ig_accounts?: unknown;
  society_id?: string | null;
  /** activity_date + 1 calendar month, computed by the backend */
  deadline?: string | null;
  compensation_receipt_url?: string | null;
  compensation_amount?: string | null;
  compensation_approval_status?: string | null;
  compensation_approval_time?: string | null;
  compensation_approval_by?: string | null;
  compensation_reject_reason?: string | null;
  tasks: RedeemedSponsorTask[];
};

export type RedeemedSponsor = {
  id: string;
  name?: string | null;
  description?: string | null;
  image_url?: string | null;
  /** `sponsors.user_id` — the business account to chat with */
  user_id?: string | null;
  activity: RedeemedSponsorActivity | null;
};

export type GetRedeemedSponsorsResult = {
  sponsors: RedeemedSponsor[];
  total: number;
  page: number;
  pageSize: number;
};

/**
 * Fetch a page of sponsors the current student has redeemed.
 * Backend: GET /api/sponsor/getRedeemedSponsors?student_id&page&pageSize
 * Returns status -40004 when the student has no redeemed sponsor record.
 */
export const GetRedeemedSponsorsAPI = async (
  page: number = 1,
  pageSize: number = 10,
): Promise<ServiceResponse<GetRedeemedSponsorsResult>> => {
  const { student } = store.getState().auth as any;
  try {
    const resp = (await service.call("GET", "api/sponsor/getRedeemedSponsors", {
      student_id: student?.id,
      page,
      pageSize,
    })) as ServiceResponse<GetRedeemedSponsorsResult>;
    if (resp?.status !== 0) {
      return { status: resp?.status ?? -1 };
    }
    return {
      status: 0,
      result: {
        sponsors: Array.isArray(resp.sponsors) ? resp.sponsors : [],
        total: Number(resp.total ?? 0),
        page: Number(resp.page ?? page),
        pageSize: Number(resp.pageSize ?? pageSize),
      },
    };
  } catch (err) {
    console.error(`GetRedeemedSponsorsAPI ${err}`);
    return { status: -1, error: err };
  }
};
